import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';


import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { UserProvider, useUser } from './context/UserContext';
import {
  WebstoreUserProvider,
} from './context/webstoreUserContext';

import Navbar from './components/navBar/navBar';
import SideBar from './components/sideBar/sideBar';

import Login from './pages/Login/Login';
import Portfolio from './pages/Portfolio/Portfolio';
import PresentationView from './pages/Presentation/PresentationView';
import Showcase from './pages/Showcase/Showcase';
import Dashboard from './pages/Dashboard/Dashboard';
import Projects from './pages/Projects/Projects';
import Links from './pages/Links/Links';
import Codes from './pages/Codes/Codes';
import Guides from './pages/Guides/Guides';
import Inbox from './pages/Inbox/Inbox';
import Dates from './pages/Dates/Dates';
import Notes from './pages/Notes/Notes';
import Images from './pages/Images/Images';
import Videos from './pages/Videos/Videos';
import Sandbox from './pages/Sandbox/Sandbox';
import Presentation from './pages/Presentation/Presentation';
import ResumePublic from './pages/Resume/ResumePublic';
import ResumeAdmin from './pages/Resume/ResumeAdmin';

import WebstoreLogin from './pages/WebstoreLogin/WebstoreLogin';
import Webstore from './pages/Webstore/Webstore';
import WebstoreAdmin from './pages/WebstoreAdmin/WebstoreAdmin';

import './styles/main.scss';

// 📏 Debug viewport dimensions (dev only)
const ViewportDebug = () => {
  const [size, setSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  React.useEffect(() => {
    const handleResize = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (process.env.NODE_ENV !== 'development') return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        backgroundColor: 'rgba(0,0,0,0.6)',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        zIndex: 9999,
      }}
    >
      {size.width} x {size.height}
    </div>
  );
};

// 🔒 Protect admin pages using your own login
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useUser();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

// 💻 Admin layout with sidebar
const ProtectedLayout = ({ children }) => {
  return <div className="protected-layout"><SideBar />{children}</div>;
};

// 🧑‍💻 Admin routes
const protectedRoutes = [
  { path: '/dashboard', element: <Dashboard /> },
  { path: '/projects', element: <Projects /> },
  { path: '/links', element: <Links /> },
  { path: '/guides', element: <Guides /> },
  { path: '/inbox', element: <Inbox /> },
  { path: '/codes', element: <Codes /> },
  { path: '/dates', element: <Dates /> },
  { path: '/notes', element: <Notes /> },
  { path: '/images', element: <Images /> },
  { path: '/videos', element: <Videos /> },
  { path: '/sandbox', element: <Sandbox /> },
  { path: '/presentations', element: <Presentation /> },
  { path: '/resume/admin', element: <ResumeAdmin /> },
  { path: '/webstore-admin', element: <WebstoreAdmin /> },
];

const AppContent = () => {
  const location = useLocation();

  const isSeperatePage =
    location.pathname.startsWith('/videos/public/') ||
    location.pathname === '/webstore';

  return (
    <>
      {!isSeperatePage && <Navbar />}

      <Routes>
        {/* 🌐 Public presentation view */}
        <Route path="/videos/public/:title" element={<PresentationView />} />

        {/* 🔐 Admin Routes */}
        {protectedRoutes.map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <ProtectedLayout>{element}</ProtectedLayout>
              </ProtectedRoute>
            }
          />
        ))}

        {/* 🌍 Public Routes */}
        <Route path="/" element={<Portfolio />} />
        <Route path="/login" element={<Login />} />
        <Route path="/showcase" element={<Showcase />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/resume" element={<ResumePublic />} />

        {/* 🛍 Webstore Routes with shared webstore context */}
        <Route
          path="/webstore-login"
          element={
            <WebstoreUserProvider>
              <WebstoreLogin />
            </WebstoreUserProvider>
          }
        />
        <Route
          path="/webstore"
          element={
            <WebstoreUserProvider>
              <Webstore />
            </WebstoreUserProvider>
          }
        />

        {/* ❗Catch-all fallback */}
        <Route path="*" element={<Navigate to="/portfolio" replace />} />
      </Routes>

      <ViewportDebug />
      <ToastContainer position="bottom-right" autoClose={3000} />
    </>
  );
};

const App = () => (
  <UserProvider>
    <Router>
      <AppContent />
    </Router>
  </UserProvider>
);

export default App;
